import { Nullable } from 'models/helpers'
import { logger } from 'utils/logging'

/** A product as an API wire needs to identify it: by its Product2 code and nothing else. */
export interface IdentifiableProduct {
  /** Stable Product2 code — the only identifier the upsell wires accept. */
  productCode: string
}

/** Ids that let a failure event be matched to the request it belongs to. */
export type ProductIdentifierCorrelation = Record<string, Nullable<string> | Nullable<number>>

/** Where an identifier decision is logged from, and how its messages read. */
export interface ProductIdentifierContext {
  /** Sentence-leading name of the flow, e.g. `Gallery staging upsell`. */
  flow: string
  /** Component name every log of this flow is filed under. */
  component: string
  /** Action name every log of this flow is filed under. */
  action: string
  /** Ids carried into every log this flow emits. */
  correlation?: ProductIdentifierCorrelation
}

/** Thrown when a product carries no usable product code, so no request can be built for it. */
export class ProductIdentifierError extends Error {
  constructor(public readonly flow: string, public readonly role: string) {
    super(`${flow}: the ${role} product carries no product code`)
    this.name = 'ProductIdentifierError'
  }
}

/**
 * Narrows a product whose Product2 code the API types as possibly absent into the wire shape, or
 * null when it carries no usable code. Callers narrow once, before the mutation, so a product
 * without a code aborts the flow instead of reaching a request the API is bound to reject.
 */
export const identifiableProduct = (productCode: Nullable<string>): Nullable<IdentifiableProduct> => {
  const code = productCode?.trim()

  return !!code ? { productCode: code } : null
}

/**
 * Serializes one product into the single code field its wire expects. The legacy numeric id is
 * never sent: the API identifies a product by its Product2 code alone, and rejects a blank one.
 *
 * @throws ProductIdentifierError when the code is missing or blank, so the caller aborts rather
 * than sending a request the API is bound to reject.
 */
export const identifierFor = <CodeKey extends string>(
  product: IdentifiableProduct,
  codeKey: CodeKey,
  role: string,
  { flow, component, action, correlation = {} }: ProductIdentifierContext
): { [K in CodeKey]: string } => {
  const productCode = product.productCode?.trim()

  if (productCode) return { [codeKey]: productCode } as { [K in CodeKey]: string }

  const codeState = !product.productCode ? 'missing' : 'blank'

  logger.error(`${flow}: ${role} product has a ${codeState} productCode`, undefined, {
    context: { component, action, metadata: { role, codeState, ...correlation } },
  })

  throw new ProductIdentifierError(flow, role)
}
